/* eslint-disable no-underscore-dangle, no-constant-condition */

import logEvents from './logEvents';
import { codes } from './socketCloseCodes';
import Timer from './Timer';

const { CLOSE_UNSUPPORTED, CLOSE_UNEXPECTED_CONDITION, CLOSE_TIMEOUT } = codes;

const unbindEventsForOldObject = (loggerDebug, obj, eventList) => {
  eventList.forEach((eventName) => {
    // eslint-disable-next-line no-param-reassign
    obj[`on${eventName}`] = (...args) => {
      loggerDebug('old object event', { obj, eventName, args });
    };
  });
};

const drainAndCloseWebSocket = (
  webSocket,
  {
    code,
    reason,
    BUFFER_DRAIN_INTERVAL = 100,
    // The total number of times to retest the websocket's send buffer
    BUFFER_DRAIN_MAX_RETRIES = 10,
  } = {}
) => {
  let attemptsRemaining = BUFFER_DRAIN_MAX_RETRIES;

  let pollId;

  const poll = () => {
    attemptsRemaining -= 1;

    if (attemptsRemaining === 0 || !(webSocket.bufferedAmount > 0)) {
      clearTimeout(pollId);

      if (code === undefined && reason === undefined) {
        // Because IE
        webSocket.close();
      } else {
        webSocket.close(code, reason);
      }
    } else {
      pollId = setTimeout(poll, BUFFER_DRAIN_INTERVAL);
    }
  };

  poll();
};

export default ({
  EventEmitter,
  WebSocket,
  Logger,
  allocateId,
}) => class ReconnectableSocket extends EventEmitter {
  static CONNECTING = WebSocket.CONNECTING;
  static OPEN = WebSocket.OPEN;
  static CLOSING = WebSocket.CLOSING;
  static CLOSED = WebSocket.CLOSED;

  url;

  // The public .reconnecting is slightly different from the private ._reconnecting because of the
  // edge case of failure in the initial connect, which follows the same reconnection logic but
  // isn't considered reconnection by the user. For user-visible reconnections, ReconnectableSocket
  // must appear OPEN, because this is the whole point of the abstraction.
  // TODO: This kind of thing is here to preserve prior behavior. It might make more sense to just
  // fail if the first connect doesn't work.
  get reconnecting() {
    return this._opened && this._reconnecting;
  }

  get readyState() {
    if (!this._webSocket || (this._reconnecting && !this._opened)) {
      return WebSocket.CONNECTING;
    }

    if (this.reconnecting) {
      return WebSocket.OPEN;
    }

    if (this._draining && this._webSocket.readyState !== WebSocket.CLOSED) {
      return WebSocket.CLOSING;
    }

    return this._webSocket.readyState;
  }

  get binaryType() { return this._webSocket.binaryType; }
  get bufferedAmount() { return this._webSocket.bufferedAmount; }
  get protocol() { return this._webSocket.protocol; }
  get extensions() { return this._webSocket.extensions; }

  _getUrl;

  _id = allocateId();
  _logger = new Logger(`OT:ReconnectableSocket:${this._id}`);
  _webSocket;

  _connectTimer;

  _pingTimer;
  _pingWarningTimer;

  _disconnectTimer;
  _reconnectMaxTimer;

  _queuedMessages = [];

  _draining = false;
  _localClose = false;
  _reconnecting = false;
  _opened = false;

  _lastCloseCode;

  constructor(opt) {
    const {
      url,

      // Max time for opening a websocket
      connectTimeout = 15000,

      // Time without sending a message after which a ping should be sent (needsPing is emitted for
      // user to take care of this).
      pingThreshold = 3000,

      // After emitting needsPing, we check after this delay whether a message was sent, if not, a
      // warning is emitted.
      pingWarningDelay = 100,

      // With reconnections, a disconnect may be followed by reconnecting, otherwise we go straight
      // to closed.
      disconnectThreshold = 5900,

      // With reconnections, we wait before reconnecting in case we also want to close it locally.
      forceDisconnectThreshold = 1000,

      // Set this to 0 for no reconnections.
      reconnectMaxDuration = 60000,
    } = (typeof opt === 'string' ? { url: opt } : opt);

    if (!url) {
      throw new Error('Must provide url');
    }

    super();

    logEvents({
      logger: this._logger,
      obj: this,
      eventNames: [
        'error',
        'open',
        'message',
        'needsPing',
        'reconnecting',
        'reconnectAttempt',
        'reconnectFailure',
        'reconnected',
        'close',
      ],
    });

    this._getUrl = typeof url === 'function' ? url : () => url;

    const createTimer = (name, duration) => new Timer({
      name,
      logger: this._logger,
      duration,
    });

    this._connectTimer = createTimer('connect', connectTimeout);
    this._pingTimer = createTimer('ping', pingThreshold);
    this._pingWarningTimer = createTimer('pingWarning', pingWarningDelay);
    this._disconnectTimer = createTimer('disconnect', disconnectThreshold);
    this._forceDisconnectTimer = createTimer('forceDisconnect', forceDisconnectThreshold);

    this._reconnectMaxTimer = (reconnectMaxDuration ?
      createTimer('reconnectMax', reconnectMaxDuration) :
      undefined
    );

    this._resetWebSocket();
  }

  send(data, retryAfterReconnect = true) {
    // send deviates from WebSocket a little in that it returns a status instead of nothing.
    let status;

    if (this.readyState === ReconnectableSocket.OPEN) {
      this._resetPingTimers();
    }

    // Allowing for the special case that we've begun reconnecting but the WebSocket is still OPEN
    // and we don't want to retryAfterReconnect - this is the last chance to send on the WebSocket
    // that is going away. This occurs when synchronously sending a message on the reconnecting or
    // reconnectAttempt events.
    const lastChanceSend = (
      this.reconnecting &&
      !retryAfterReconnect &&
      this._webSocket.readyState === WebSocket.OPEN
    );

    if (this.reconnecting && !lastChanceSend) {
      if (retryAfterReconnect) {
        this._queuedMessages.push(data);
        status = 'queued';
      } else {
        this._logger.debug(
          'Dropping message during reconnection since retryAfterReconnect is false:',
          data
        );

        status = 'dropped';
      }
    } else {
      // WebSocket throws INVALID_STATE_ERR if you try to send when it's closed. We preserve that
      // behavior rather than adding handling for it. Reconnectable socket should preserve
      // WebSocket's behavior where it can, except for the different constructor and event
      // interface.

      try {
        this._webSocket.send(data);
      } catch (err) {
        if (this._webSocket.readyState === WebSocket.OPEN) {
          // On the other hand, there is a known bug in Firefox where .send throws an exception
          // even though it's open:
          // https://bugzilla.mozilla.org/show_bug.cgi?id=1204727
          this._logger.debug('webSocket.send threw exception even though it was open:', err);
        } else {
          throw err;
        }
      }

      status = 'sent';
    }

    return status;
  }

  close(code, reason) {
    if (
      (
        this.readyState === ReconnectableSocket.CLOSING ||
        this.readyState === ReconnectableSocket.CLOSED
      ) &&
      !this._forceDisconnectTimer.isRunning()
    ) {
      // close() does nothing if already closing/closed
      return;
    }

    if (this._forceDisconnectTimer.isRunning()) {
      this.emit('close', { code, reason });
    }

    this._clearTimers();
    this._draining = true;
    this._localClose = true;

    drainAndCloseWebSocket(this._webSocket, { code, reason });
  }

  _emitReconnectionEvent(eventName, ...args) {
    if (this._opened) {
      this.emit(eventName, ...args);
    } else {
      this._logger.debug('pre-open reconnection event:', eventName, ...args);
    }
  }

  _resetWebSocket() {
    if (this.readyState === WebSocket.CLOSING || this.readyState === WebSocket.CLOSED) {
      throw new Error('CLOSED is terminal, shouldn\'t reset.');
    }

    this._clearPingTimers();
    this._disconnectTimer.clear();
    this._forceDisconnectTimer.clear();
    this._connectTimer.clear();

    const oldWebSocket = this._webSocket;
    this._webSocket = undefined;

    const newWebSocket = new WebSocket(this._getUrl());
    newWebSocket.binaryType = 'arraybuffer';
    newWebSocket.onopen = event => this._webSocketOpened(event);
    newWebSocket.onmessage = event => this._webSocketMessage(event);
    newWebSocket.onclose = event => this._webSocketClosed(event);
    newWebSocket.onerror = event => this._webSocketError(event);
    this._webSocket = newWebSocket;

    this._resetConnectTimer();

    if (oldWebSocket) {
      unbindEventsForOldObject(
        (...args) => this._logger.spam(...args),
        oldWebSocket, [
          'open',
          'message',
          'close',
          'error',
        ]
      );

      drainAndCloseWebSocket(oldWebSocket);
    }
  }

  _webSocketOpened() {
    this._resetPingTimers();
    this._resetDisconnectTimer();
    this._connectTimer.clear();

    const wasReconnecting = this._reconnecting;
    this._reconnecting = false;

    if (wasReconnecting) {
      while (true) {
        const msg = this._queuedMessages.shift();
        if (!msg) {
          break;
        }

        this.send(msg, true);
      }

      this._reconnectMaxTimer.clear();
      this._emitReconnectionEvent('reconnected');
    }

    if (!this._opened) {
      this._opened = true;
      this.emit('open');
    }
  }

  _webSocketMessage(event) {
    if (this.readyState !== ReconnectableSocket.CLOSING) {
      // Without this you can receive a message while we are closing the socket and we end up
      // trying to reconnect again afterwards
      this._resetDisconnectTimer();
    }
    this.emit('message', event);
  }

  _webSocketClosed(closeEvent) {
    this._lastCloseCode = closeEvent.code;
    const dontReconnectCodes = [CLOSE_UNSUPPORTED, CLOSE_UNEXPECTED_CONDITION];

    const shouldReconnect = () => (
      !this._localClose &&
      dontReconnectCodes.indexOf(closeEvent.code) === -1 &&
      this._reconnectMaxTimer
    );

    if (shouldReconnect()) {
      // We wait a short period of time in case the socket was closed from the other side and
      // we also want to close it from our side. This avoids an unnecessary reconnect attempt.
      this._forceDisconnectTimer.reset(() => {
        if (shouldReconnect()) {
          this._reconnect(closeEvent);
        } else {
          this.emit('close', closeEvent);
        }
      });
    } else {
      this.emit('close', closeEvent);
    }
  }

  _webSocketError(event) {
    // TODO: handle possible reconnection
    this.emit('error', event);
  }

  _clearPingTimers() {
    [
      this._pingTimer,
      this._pingWarningTimer,
    ].forEach(timer => timer.clear());
  }

  _clearTimers() {
    [
      this._connectTimer,
      this._pingTimer,
      this._pingWarningTimer,
      this._disconnectTimer,
      this._forceDisconnectTimer,
      this._reconnectMaxTimer,
    ].forEach(timer => timer && timer.clear());
  }

  _resetConnectTimer() {
    this._connectTimer.reset(() => {
      if (this._reconnectMaxTimer) {
        this._reconnect();
      } else {
        this.emit('error', new Error('connect timed out'));
        this.close(CLOSE_TIMEOUT);
      }
    });
  }

  _resetPingTimers() {
    this._pingWarningTimer.clear();

    this._pingTimer.reset(() => {
      this._pingWarningTimer.reset(() => {
        this._logger.warn('Did not send message after needsPing event. Other side may ' +
          'disconnect.');
      });

      this.emit('needsPing');
    });
  }

  _reconnect(event) {
    if (!this._reconnecting) {
      this._reconnecting = true;
      this._emitReconnectionEvent('reconnecting', event);

      this._reconnectMaxTimer.reset(() => {
        // Reconnect max duration expired
        this._reconnecting = false;
        this.close(this._lastCloseCode);
        this._emitReconnectionEvent('error', new Error('reconnect failure'));
        this._emitReconnectionEvent('reconnectFailure', event);
      });

      // _reconnectMaxTimer is cleared elsewhere instead of using the reconnected event because it
      // needs to also be cleared on pre-open reconnected which suppresses the event
    }

    this._emitReconnectionEvent('reconnectAttempt', event);
    this._resetWebSocket();
  }

  _resetDisconnectTimer() {
    this._disconnectTimer.reset(() => {
      if (this._reconnectMaxTimer) {
        this._reconnect();
      } else {
        // Reconnections disabled
        this.close();
      }
    });
  }
};



// WEBPACK FOOTER //
// ./src/js/RaptorSession/ReconnectableSocket.js